import type { ScaleOrdinal, Series, SeriesPoint } from 'd3';
import { format as d3Format, scaleOrdinal, stack } from 'd3';
import { useLayoutEffect, useRef, useState } from 'react';

import { type CommonPlotProps, FormatType } from '../../types';
import { getFormatter, getOtherColor } from '../../utilities';
import { createCombinedLegendFormatter } from '../../utilities/create-combined-legend-formatter';
import type { BarData } from '../bar/bar-chart';
import { Legend } from '../legend/legend';
import { stackedSegmentShare, stackedSegmentValue } from './stacked-bar-scale';

export const Dimensions = {
  LabelPadding: 5,
  MaxBarThickness: 40,
  MinBarThickness: 4,
  BandPaddingFactor: 0.2,
  SegmentSpacing: 2,
  LegendHeight: 42,
  NewLegendTopMargin: 14,
  XAxisLabelHeight: 28,
  NewBandPaddingInner: 0.54,
  NewBandPaddingOuter: 0.2,
  NewSegmentSpacing: 3,
} as const;

type StackedBarOrientation = 'horizontal' | 'vertical';

export type StackedBarData<TData extends object = object> = {
  group: string;
  values: BarData<TData>[];
};

export type StackedBarDataIndexed = {
  label: string;
  segments: Record<string, number>;
  _index: number;
};

export type StackedBarChartProps = {
  data: StackedBarData[];
  format?: FormatType;
  colorOverride?: Record<string, string>;
  colorPalette?: string[];
  hideLegend?: boolean;
  formatLegendLabel?: (label: string) => string;
  maxLabelWidth?: number;
  maxLegendLabelWidth?: number;
  segmentOrder?: string[];
  normalized?: boolean;
  orientation?: StackedBarOrientation;
  isTimeseries?: boolean;
  highlightMatchingSegments?: boolean;
  /**
   * Drilldown affordance (themed appearances, horizontal orientation only):
   * hovering keeps the hovered category coloured while every other segment
   * flattens to alternating neutral fills; clicking a segment reports its
   * category label and colour.
   */
  onSegmentClick?: (label: string, color?: string) => void;
} & CommonPlotProps;

type StackedBarModel = {
  dataIndexed: StackedBarDataIndexed[];
  displayLabels: string[];
  segmentLabels: string[];
  stackedSeries: Series<StackedBarDataIndexed, string>[];
  emptyBars: StackedBarDataIndexed[];
  colorScale: ScaleOrdinal<string, string, string>;
  domainMax: number;
  noData: boolean;
};

export type StackedBarRendererProps = {
  model: StackedBarModel;
  width: number;
  height: number;
  format: FormatType;
  hideLegend?: boolean;
  maxLabelWidth: number;
  formatLegendLabel: (label: string) => string;
  maxLegendLabelWidth?: number;
  isTimeseries?: boolean;
  highlightMatchingSegments?: boolean;
  onSegmentClick?: (label: string, color?: string) => void;
};

type ProcessOptions = {
  segmentOrder?: string[];
  normalized: boolean;
  colorOverride?: Record<string, string>;
  colorPalette: string[];
};

export const processStackedBarData = (
  data: StackedBarData[],
  { segmentOrder, normalized, colorOverride, colorPalette }: ProcessOptions
): StackedBarModel => {
  /* Flatten nested values into the internal segment record. */
  const dataIndexed: StackedBarDataIndexed[] = data.map((bar, idx) => ({
    label: bar.group,
    segments: bar.values.reduce<Record<string, number>>(
      (acc, v) => ({ ...acc, [v.label]: v.value ?? 0 }),
      {}
    ),
    _index: idx,
  }));

  const labelCounts = new Map<string, number>();
  const displayLabels = dataIndexed.map((bar) => {
    const count = labelCounts.get(bar.label) ?? 0;
    labelCounts.set(bar.label, count + 1);
    return count === 0 ? bar.label : `${bar.label} (${count + 1})`;
  });

  const observedSegmentLabels = Array.from(
    new Set(data.flatMap((bar) => bar.values.map((v) => v.label)))
  );
  const segmentLabels = (() => {
    if (segmentOrder === undefined) return observedSegmentLabels;
    const orderedKnown = segmentOrder.filter((label) =>
      observedSegmentLabels.includes(label)
    );
    const extras = observedSegmentLabels.filter(
      (label) => !segmentOrder.includes(label)
    );
    return [...orderedKnown, ...extras];
  })();

  const emptyBars = dataIndexed.filter((bar) =>
    Object.values(bar.segments).every((value) => value === 0)
  );

  const stackedSeries = stack<StackedBarDataIndexed>()
    .keys(segmentLabels)
    .value((d, key) => stackedSegmentValue(d.segments, key, normalized))(
    dataIndexed
  );

  const overridenColors = Object.values(colorOverride ?? {});
  const colors = colorPalette.filter(
    (color) => !overridenColors.includes(color)
  );
  const labelColors = segmentLabels.map((label, index) => ({
    label,
    color: colorOverride?.[label] ?? colors[index] ?? getOtherColor(),
  }));
  const colorScale = scaleOrdinal<string>()
    .domain(labelColors.map(({ label }) => label))
    .range(labelColors.map(({ color }) => color))
    .unknown(getOtherColor());

  const noData =
    data.length === 0 ||
    data.every((metric) => metric.values.length === 0) ||
    data.every((metric) =>
      metric.values.every((value) => (value.value ?? 0) === 0)
    );

  return {
    dataIndexed,
    displayLabels,
    segmentLabels,
    stackedSeries,
    emptyBars,
    colorScale,
    domainMax: normalized
      ? 1
      : Math.max(
          1,
          ...data.map((bar) =>
            bar.values.reduce((acc, v) => acc + (v.value ?? 0), 0)
          )
        ),
    noData,
  };
};

export const buildSegmentEntries = (
  series: Series<StackedBarDataIndexed, string>,
  active: SeriesPoint<StackedBarDataIndexed>,
  format: FormatType,
  focusHoveredOnly = false
) => {
  const formatMain = getFormatter(format);
  const formatSecondary = getFormatter(FormatType.PERCENTAGE);
  // Tiny non-zero shares would read "0%" — surface up to two decimals instead.
  const formatShare = (share: number) => {
    const text = formatSecondary(share);
    return text === '0%' && share > 0 ? d3Format('.2~%')(share) : text;
  };
  const entries = series.map((s) => ({
    active: s.data.label === active.data.label,
    label: s.data.label,
    mainValue: formatMain(s.data.segments[series.key] ?? 0),
    secondaryValue: formatShare(
      stackedSegmentShare(s.data.segments, series.key)
    ),
  }));
  return focusHoveredOnly ? entries.filter((entry) => entry.active) : entries;
};

type StackedBarLegendProps = {
  segmentLabels: string[];
  colorScale: ScaleOrdinal<string, string, string>;
  formatLegendLabel: (label: string) => string;
  maxLegendLabelWidth?: number;
  marginLeft?: number;
  className?: string;
  highlightedLabel?: string | null;
};

export const StackedBarLegend = ({
  segmentLabels,
  colorScale,
  formatLegendLabel,
  maxLegendLabelWidth,
  marginLeft,
  className,
  highlightedLabel = null,
}: StackedBarLegendProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [singleRowHeight, setSingleRowHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    const legend = containerRef.current?.firstElementChild;
    if (!(legend instanceof HTMLElement)) {
      setSingleRowHeight(null);
      return;
    }

    const measure = () => {
      const items = Array.from(legend.children).filter(
        (child): child is HTMLElement => child instanceof HTMLElement
      );
      const rowTops = new Set(items.map((item) => item.offsetTop));
      if (items.length < 2 || rowTops.size <= 2) {
        setSingleRowHeight(null);
        return;
      }
      const firstTop = Math.min(...rowTops);
      const firstRowHeight = Math.max(
        ...items
          .filter((item) => item.offsetTop === firstTop)
          .map((item) => item.offsetHeight)
      );
      const legendStyle = window.getComputedStyle(legend);
      const padY =
        (Number.parseFloat(legendStyle.paddingTop) || 0) +
        (Number.parseFloat(legendStyle.paddingBottom) || 0);
      setSingleRowHeight(firstRowHeight + padY);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(legend);
    return () => observer.disconnect();
  }, []);

  const combinedLegendFormatter = createCombinedLegendFormatter(
    formatLegendLabel,
    maxLegendLabelWidth
  );

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        maxHeight: singleRowHeight ?? Dimensions.LegendHeight,
        ...(marginLeft !== undefined && { marginLeft: `${marginLeft}px` }),
      }}
    >
      <Legend
        items={segmentLabels.map((label) => ({
          label,
          color: colorScale(label),
        }))}
        formatLabel={combinedLegendFormatter}
        highlightedLabel={highlightedLabel}
      />
    </div>
  );
};
